import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { adobeAnalytics } from 'adobe-analytics';

import { PREQUAL_JSON } from '../../assets/constants';
import { ChatWidgetService } from '../services/chat-widget.service';
import {
  InventoryApplication,
  InventoryModuleScriptLoaderService,
} from '../services/inventory-module-script-loader.service';
import { PrequalService } from '../services/prequal.service';
import { QualtricsScriptLoaderService } from '../services/qualtrics-script-loader.service';
import { RuntimeConfigService } from '../services/runtime-config';
import { ScrollingService } from '../services/scrolling.service';
import { DealerApproval } from '../services/submit-caps-lead-rest.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { DealerLocation } from '../shared/models/prequal.types';
import { FinalSuccessEventsService } from '../shared/service/final-success-events.service';
import { portalApplicationId } from '../shared/state/local-cache';

@Component({
  selector: 'capq-final-success-response',
  templateUrl: './final-success-response.component.html',
  styleUrls: ['./final-success-response.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FinalSuccessResponseComponent
  extends Unsubscribe
  implements OnInit, OnChanges, AfterViewInit
{
  congratsImg = 'assets/images/congrats.png';
  formFieldData: any;
  logoURL = './assets/images/CA_Logo.svg';
  closeURL = './assets/images/icn_close.png';
  /** DOM id the embed renders into; passed to `InventoryModule.render`. */
  readonly embedTargetId = 'cac-inventory-module-embed';
  /**
   * Whether to display the inventory-module embed. Seeded in `ngOnInit` from
   * the feature flag + whether at least one matched dealer has inventory to
   * show (a populated `inventoryUrl`); flipped to false only if the embed
   * script fails to load or reports an internal error — NOT on sparse vehicle
   * count.
   */
  showInventoryModule = false;

  /**
   * Stable, init-time flag: true when the DVM inventory experience applies at
   * all (feature flag on + at least one matched pilot dealer). Unlike
   * `showInventoryModule`, this is set once and NEVER flipped, so it survives
   * the embed hiding itself on a sparse/failed load. Drives the legacy-vs-new
   * subtitle/contact copy and whether the congrats image is shown. See
   * SVRS-48821.
   */
  inventoryExperienceActive = false;

  /**
   * Drives the intro heading above the embed:
   * - `available` (>= minInventoryVehicles): render the intro heading.
   * - `sparse` (< minInventoryVehicles): hide the heading; embed continues to render.
   * - `pending`/`failed`: render neither.
   */
  inventoryDisplayState: 'pending' | 'available' | 'sparse' | 'failed' =
    'pending';

  /** Min vehicles the embed must render to be treated as "available". */
  private readonly minInventoryVehicles = 3;

  private readonly baseInventoryUrls = new Map<string, string>();
  private applications: InventoryApplication[] = [];
  private _dealerApprovals: DealerApproval[] = [];
  /** True once the embed render has been kicked off in `ngAfterViewInit`. */
  private inventoryModuleRendered = false;

  @Input() adobeScriptLoaded = false;
  @Input() dealers: DealerLocation[] = [];
  @Input() shouldLoadChatWidget = false;
  @Input() inventoryModuleEnabled = false;

  /**
   * Per-dealer caps-lead approvals. Modelled as a setter so the embed update is
   * driven by the data actually arriving rather than by `ngOnChanges` bookkeeping:
   * the initial value is consumed by the first render in `ngOnInit` /
   * `ngAfterViewInit`, and any value that lands *after* the embed has rendered
   * (the max timeout elapsed before the caps-lead response) is injected live via
   * `setOptions` — but only when it carries usable lead IDs.
   */
  @Input()
  get dealerApprovals(): DealerApproval[] {
    return this._dealerApprovals;
  }
  set dealerApprovals(approvals: DealerApproval[]) {
    this._dealerApprovals = approvals ?? [];

    // Inject only after the embed has rendered and only when at least one
    // approval carries a non-empty `approvalNumber` — an empty/blank payload
    // yields no `leadId`s, so there is nothing to push to the embed.
    const hasUsableApprovals = this._dealerApprovals.some(
      (a) => !!a.approvalNumber
    );
    if (this.inventoryModuleRendered && hasUsableApprovals) {
      this.applications = this.buildApplications();
      this.inventoryModuleLoader.updateApplications(this.applications);
      this.enrichInventoryUrls();
    }
  }

  @ViewChild('topElement') topElement!: ElementRef<HTMLDivElement>;

  constructor(
    private readonly scrollService: ScrollingService,
    private readonly prequalService: PrequalService,
    private readonly finalSuccessEventsService: FinalSuccessEventsService,
    private readonly inventoryModuleLoader: InventoryModuleScriptLoaderService,
    private readonly qualtricsScriptLoader: QualtricsScriptLoaderService,
    private readonly chatWidgetService: ChatWidgetService,
    private readonly runtimeConfigService: RuntimeConfigService,
    private readonly ngZone: NgZone,
    private readonly cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.formFieldData = PREQUAL_JSON?.finalSuccess;

    this.qualtricsScriptLoader.load();
    this.maybeLoadChatWidget();

    this.adobeScriptLoaded &&
      adobeAnalytics.emitEvent('cac-page-view', null, {
        form_design: 'v 1.0',
        section_name: 'dealers connect',
        form_submission_id: (portalApplicationId() || '').toString(),
        dealer_list: '',
        form_submission_status: 'success',
        form_failure_reason: undefined,
        event_type: 'cac-page-view',
      });
    this.finalSuccessEventsService.triggerEvent('showDealerWebsite');

    // Decide up front (before the view is checked) whether the inventory
    // experience applies at all: flag on + at least one matched dealer that
    // actually has inventory. Seed both the stable experience flag and the
    // mutable embed-visibility flag from this single evaluation.
    const experienceActive = this.shouldShowInventoryModule();
    this.inventoryExperienceActive = experienceActive;
    this.showInventoryModule = experienceActive;

    // Capture raw inventory URLs before enrichment so re-enrichment never double-appends.
    this.dealers.forEach((d) => {
      if (d.dealerId && d.inventoryUrl) {
        this.baseInventoryUrls.set(d.dealerId, d.inventoryUrl);
      }
    });
    this.applications = this.buildApplications();
    this.enrichInventoryUrls();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['shouldLoadChatWidget']) {
      this.maybeLoadChatWidget();
    }
  }

  ngAfterViewInit(): void {
    this.scrollService.scrollToTop(this.topElement.nativeElement);
    // Render after the view (and the embed target element) is in the DOM.
    this.loadInventoryModule();
  }

  // Mount the chat facade only once the parent confirms a lead was created
  // (leadCreated:true on the caps-lead response). ChatWidgetService is
  // idempotent, so re-entry from ngOnChanges + ngOnInit is safe.
  private maybeLoadChatWidget(): void {
    if (!this.shouldLoadChatWidget) return;
    const appId = portalApplicationId();
    if (!appId) return;
    this.chatWidgetService.load({ applicationId: appId });
  }

  navigateToHome() {
    document.location.href = document.location.origin;
  }

  onInventoryLinkClick(dealer: DealerLocation): void {
    window.adobeDataLayer.push({
      event: 'cac-interaction',
      interaction: {
        location: 'dealers-connect',
        label: "view-dealer's-inventory",
      },
      dealers: [`${dealer.dealerId}`],
    });
  }

  /**
   * Gets the user's address from residence data
   * @returns Object with address information from residence
   */
  getUserResidenceAddress(): {
    addressLine1: string;
    city: string;
    state: string;
    zipCode?: string;
  } {
    const residenceData = this.prequalService.getResidenceData();

    if (residenceData) {
      return {
        addressLine1: residenceData.addressLine1 || '',
        city: residenceData.city || '',
        state: residenceData.userState || '',
        zipCode: residenceData.zipCode || '',
      };
    }

    return {
      addressLine1: '',
      city: '',
      state: '',
      zipCode: '',
    };
  }

  /**
   * Gets user's city and state from residence data for dealer finder URL
   * @returns URL string with user's location parameters
   */
  getDealerFinderUrlWithUserLocation(): string {
    const baseUrl = '/car-buyers/dealer-finder';
    const userAddress = this.getUserResidenceAddress();

    if (userAddress.state && userAddress.city) {
      const locationFormat = `${userAddress.state}-${userAddress.city}`;
      return `${baseUrl}/${locationFormat}`;
    }

    // Fallback to dealer location if user address not available
    return baseUrl;
  }

  private loadInventoryModule(): void {
    if (!this.showInventoryModule) return;

    // From here on, late-arriving approvals are injected via the setter's
    // setOptions path rather than the initial render.
    this.inventoryModuleRendered = true;

    this.inventoryModuleLoader.loadInventoryModule({
      target: `#${this.embedTargetId}`,
      applications: this.applications,
      consumerZip: this.prequalService.getResidenceData()?.zipCode ?? '',
      make: this.stripAny(this.prequalService.getVehicleMake()),
      bodyStyle: this.stripAny(this.prequalService.getVehicleType()),
      pageSize:
        this.runtimeConfigService.prequalConfig['INVENTORY_MODULE_PAGE_SIZE'],
      onError: (err) => this.onInventoryModuleError(err),
      onScriptError: () => this.onInventoryModuleScriptError(),
      onLoad: (meta) => this.onInventoryModuleLoad(meta),
    });
  }

  /**
   * The embed finished loading. Sets `inventoryDisplayState` based on vehicle
   * count: `>= minInventoryVehicles` shows the intro heading (`available`);
   * fewer hides only the heading (`sparse`) — the embed continues to render.
   * `showInventoryModule` is not changed here; only error callbacks suppress
   * the embed. Runs inside the zone because the embed fires its callbacks
   * outside Angular's zone (mirrors `hideInventoryModule`).
   */
  private onInventoryModuleLoad(meta: { vehicleCount: number }): void {
    this.ngZone.run(() => {
      if ((meta?.vehicleCount ?? 0) >= this.minInventoryVehicles) {
        this.inventoryDisplayState = 'available';
      } else {
        this.inventoryDisplayState = 'sparse';
      }
      this.cdr.markForCheck();
    });
  }

  /**
   * The embed is shown only when the feature flag is on AND at least one
   * matched dealer has inventory to display (a populated `inventoryUrl`).
   * The widget handles its own "fewer than N vehicles" presentation.
   */
  private shouldShowInventoryModule(): boolean {
    if (!this.inventoryModuleEnabled) return false;
    const hasInventory = this.dealers?.some((d) => !!d.inventoryUrl) ?? false;
    const hasDealerId = this.dealers?.some((d) => !!d.dealerId) ?? false;
    return hasInventory && hasDealerId;
  }

  /**
   * Builds the per-dealer `applications` array for the embed: `dealerId` +
   * `submissionId` (portalApplicationId) + `openAccount` (our open-loan
   * indicator), plus `leadId` (the dealer's caps-lead approval number) when
   * one was returned.
   */
  private buildApplications(): InventoryApplication[] {
    const openAccount = this.prequalService.getOpenLoan();
    const submissionId = (portalApplicationId() || '').toString() || undefined;
    const approvalByDealer = new Map(
      this.dealerApprovals.map((a) => [a.dealerId, a.approvalNumber])
    );

    return this.dealers
      .filter((d): d is DealerLocation & { dealerId: string } => !!d.dealerId)
      .map((d) => {
        const application: InventoryApplication = {
          dealerId: d.dealerId,
          openAccount,
        };
        if (submissionId) {
          application.submissionId = submissionId;
        }
        const leadId = approvalByDealer.get(d.dealerId);
        if (leadId) {
          application.leadId = leadId;
        }
        return application;
      });
  }

  private onInventoryModuleError(err: { code: string; message: string }): void {
    // Surfaced to Fullstory via console.error. No PII in the payload.
    console.error('Inventory module embed error:', err.code, err.message);
    this.hideInventoryModule();
  }

  private onInventoryModuleScriptError(): void {
    // The embed script failed to load; log for Fullstory and do not show the
    // widget. No PII in the payload.
    console.error('Inventory module embed script failed to load.');
    this.hideInventoryModule();
  }

  // The embed callbacks can fire outside Angular's zone; re-enter so the
  // OnPush view reflects the visibility change.
  private hideInventoryModule(): void {
    this.ngZone.run(() => {
      this.showInventoryModule = false;
      // `failed` so neither the intro heading nor the "find inventory" link
      // renders; `inventoryExperienceActive` stays true so the new copy +
      // image-removed chrome remain (SVRS-48821 scenario 3).
      this.inventoryDisplayState = 'failed';
      this.cdr.markForCheck();
    });
  }

  private enrichInventoryUrls(): void {
    this.applications.forEach((app) => {
      const baseUrl = this.baseInventoryUrls.get(app.dealerId);
      if (!baseUrl) return;

      const dealer = this.dealers.find((d) => d.dealerId === app.dealerId);
      if (!dealer) return;

      dealer.inventoryUrl = `${baseUrl}?apps=${btoa(JSON.stringify([app]))}`;
    });
  }

  private stripAny(values: string[] | undefined): string[] | undefined {
    if (!values?.length) return undefined;
    const filtered = values.filter((v) => v.toLowerCase() !== 'any');
    return filtered.length ? filtered : undefined;
  }
}
